import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import { EMPTY_STRING } from '../../utils/usefulStrings'
import DynamicOverflowListStore from './DynamicOverflowList.store'

interface IChildrenObject {
  visibleElements: JSX.Element[]
  overflowElements: JSX.Element[]
  containerRefSetter: (elementRef: HTMLElement) => void
  setAdditionalElementsRef?(elementRef: HTMLElement): void
}

interface IProps {
  items: JSX.Element[]

  shouldIgnoreScrollBarWidth?: boolean
  shouldRecalculateRowsCount?: boolean
  allowRecalculationForItems?: boolean
  shouldUseItemKey?: boolean
  rowsCount?: number
  additionalWidth?: number
  moreButtonWidth?: number
  minContainerWidthConstraint?: number
  paddingWidth?: number
  elementsClassName?: string

  store?: DynamicOverflowListStore

  children(childrenObject: IChildrenObject): JSX.Element
}

const SCROLL_BAR_WIDTH = 20
const DEFAULT_ROWS_COUNT = 1
const MIN_ALLOWED_ELEMENTS = 1

@observer
export default class DynamicOverflowList extends React.Component<IProps> {
  public static defaultProps = {
    rowsCount: DEFAULT_ROWS_COUNT,
    additionalWidth: 0,
    moreButtonWidth: 0,
    minContainerWidthConstraint: 0,
    paddingWidth: 0,
  }

  private readonly localStore: DynamicOverflowListStore = null

  @observable private localProps: IProps = null
  private containerRef: HTMLElement
  private additionalElementsRef: HTMLElement
  private listElementsRefs: HTMLDivElement[] = []
  private containerResizeObserver: ResizeObserver | null = null

  public constructor(props: IProps) {
    super(props)

    if (!props.store) {
      this.localStore = new DynamicOverflowListStore()
    }
    this.localProps = {
      ...DynamicOverflowList.defaultProps,
      ...props,
    }
    makeObservable(this)
  }

  private get store(): DynamicOverflowListStore {
    return this.localProps.store || this.localStore
  }

  public componentDidMount() {
    this.store.disableRecalculation()

    window.addEventListener('resize', this.onComponentResized)

    this.calculateNumberOfVisibleElements()
  }

  @action
  public componentDidUpdate(prevProps: IProps) {
    const {
      items,
      rowsCount,
      shouldRecalculateRowsCount,
      allowRecalculationForItems,
    } = this.props

    if (this.props !== prevProps) {
      this.localProps = this.props
    }

    const { shouldRecalculate, enableRecalculation, disableRecalculation } =
      this.store

    const shouldRecalculateRows =
      shouldRecalculateRowsCount && prevProps.rowsCount !== rowsCount
    const shouldRecalculateItems =
      allowRecalculationForItems && prevProps.items !== items

    if (shouldRecalculate) {
      this.clearNotDisplayedElementsRefs()
      this.calculateNumberOfVisibleElements()
      disableRecalculation()
    } else if (
      prevProps.items.length !== items.length ||
      shouldRecalculateRows ||
      shouldRecalculateItems
    ) {
      enableRecalculation()
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onComponentResized)
    this.containerResizeObserver?.disconnect()
  }

  public render() {
    return this.props.children({
      visibleElements: this.visibleElements,
      overflowElements: this.overflowElements,
      containerRefSetter: this.setContainerRef,
      setAdditionalElementsRef: this.setAdditionalElementsRef,
    })
  }

  @action.bound
  private calculateNumberOfVisibleElements() {
    const {
      items,
      rowsCount,
      additionalWidth,
      moreButtonWidth,
      minContainerWidthConstraint,
    } = this.localProps

    const { setNumberOfVisibleElements, resetNumberOfVisibleElements } =
      this.store

    const containerWidth = this.getContainerWidth()

    if (containerWidth <= minContainerWidthConstraint) {
      setNumberOfVisibleElements(MIN_ALLOWED_ELEMENTS)
      return
    }

    const maxElementsAllowed = this.getMaxAllowedElementsCount(
      rowsCount,
      containerWidth,
      additionalWidth,
      moreButtonWidth,
    )

    if (maxElementsAllowed > items.length) {
      resetNumberOfVisibleElements()
      return
    }

    setNumberOfVisibleElements(
      Math.max(maxElementsAllowed, MIN_ALLOWED_ELEMENTS),
    )
  }

  @action.bound
  private onComponentResized() {
    this.calculateNumberOfVisibleElements()
    this.store.enableRecalculation()
  }

  private getMaxAllowedElementsCount(
    rowsCount: number,
    containerWidth: number,
    additionalWidth: number,
    moreButtonWidth: number,
  ): number {
    let maxElementsAllowed = 0

    Array.from(Array(rowsCount).keys()).forEach(rowIndex => {
      const lastIndex = rowsCount - 1
      const isLastRow = rowIndex === lastIndex

      const selectedRefs = this.listElementsRefs.slice(maxElementsAllowed)

      if (!selectedRefs?.length) {
        return
      }

      const elementsCountInRow = this.getElementsCountInRow(
        selectedRefs,
        containerWidth,
        additionalWidth,
        moreButtonWidth,
        isLastRow,
      )

      maxElementsAllowed += elementsCountInRow
    })

    return maxElementsAllowed
  }

  private getElementsCountInRow(
    elementsRefs: HTMLDivElement[],
    containerWidth: number,
    additionalWidth: number,
    moreButtonWidth: number,
    isLastRow: boolean,
  ): number {
    const moreBtnWidth = isLastRow ? moreButtonWidth : 0
    let elementsWidth = isLastRow
      ? this.additionalElementsRef?.offsetWidth || additionalWidth
      : 0
    let elementsInRowCount = 0
    let additionalElsCount = 0

    elementsRefs.forEach(elementRef => {
      elementsWidth += elementRef.offsetWidth

      if (elementsWidth < containerWidth - moreBtnWidth) {
        elementsInRowCount += 1
      } else if (elementsWidth < containerWidth) {
        additionalElsCount += 1
      } else {
        return
      }
    })

    if (elementsInRowCount + additionalElsCount === elementsRefs.length) {
      return elementsRefs.length
    }

    return elementsInRowCount
  }

  private getContainerWidth = (): number => {
    if (!this.containerRef || !this.containerRef.offsetWidth) {
      return 0
    }

    const { paddingWidth, shouldIgnoreScrollBarWidth } = this.localProps
    const scrollBarWidth = shouldIgnoreScrollBarWidth ? 0 : SCROLL_BAR_WIDTH

    return this.containerRef.offsetWidth - scrollBarWidth - paddingWidth
  }

  private setContainerRef = (elementRef: HTMLElement) => {
    this.containerRef = elementRef
    // Recalculate on ANY container width change, not just window resize: sibling
    // layout shifts (e.g. a widening label) otherwise leave items clipped with no
    // More button. Observing the container here gives every consumer this for
    // free instead of each wiring its own ResizeObserver.
    this.containerResizeObserver?.disconnect()
    this.containerResizeObserver = null
    if (elementRef && typeof ResizeObserver !== 'undefined') {
      this.containerResizeObserver = new ResizeObserver(this.onComponentResized)
      this.containerResizeObserver.observe(elementRef)
    }
  }

  private setAdditionalElementsRef = (elementRef: HTMLElement) => {
    this.additionalElementsRef = elementRef
  }

  private addListElementRef = (elementRef: HTMLDivElement) => {
    if (elementRef) {
      this.listElementsRefs.push(elementRef)
    }
  }

  private clearNotDisplayedElementsRefs = () => {
    this.listElementsRefs = this.listElementsRefs.filter(
      node => !!node?.offsetWidth,
    )
  }

  private get visibleElements(): JSX.Element[] {
    return this.itemsList.slice(0, this.store.numberOfVisibleElements)
  }

  private get overflowElements(): JSX.Element[] {
    return this.itemsList.slice(this.store.numberOfVisibleElements)
  }

  private get itemsList(): JSX.Element[] {
    const { items, shouldUseItemKey, elementsClassName } = this.props

    const className = `inline-block ${elementsClassName || EMPTY_STRING}`

    return items.map((item, index) => (
      <div
        className={className}
        key={shouldUseItemKey ? item.key : index}
        ref={this.addListElementRef}
      >
        {item}
      </div>
    ))
  }
}
